// Single source of truth for an employee's display status. Raw backend status +
// "out on leave today" collapse to one of three canonical states, each with a
// consistent label + theme-aware badge/dot classes.
export type EmployeeStatus = 'active' | 'on_leave' | 'inactive';

const INACTIVE = ['inactive', 'terminated', 'suspended', 'deactivated'];

// ⚠️ "ON LEAVE" COMES ONLY FROM THE LEAVE DATES. The stored employee status
// `OnLeave` was cleared by a nightly job that matched one exact date; a missed
// night (prod, Oct 2026) left people "On Leave" days after their leave ended.
// A raw `OnLeave` is therefore NOT trusted here — `isOutNow` (approved leave
// covering today, from leave-service) is the only signal.
export function resolveEmployeeStatus(raw?: string | null, isOutNow = false): EmployeeStatus {
  const r = (raw || '').toLowerCase();
  if (INACTIVE.includes(r)) return 'inactive';
  if (isOutNow) return 'on_leave';
  return 'active';
}

/** Today's date in the browser's own timezone, YYYY-MM-DD. Never
 *  `new Date().toISOString()` — that is the UTC date, which in Dar es Salaam is
 *  still YESTERDAY until 03:00. */
export function localTodayIso(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

type LeaveRow = { status?: string | null; start_date?: string | null; end_date?: string | null };

/** Whether any APPROVED leave covers `isoDate`. Dates are inclusive and
 *  date-only; a single-day or half-day leave counts for its day. Rows without
 *  a status are treated as approved (the who's-out feed returns approved only). */
export function isOnLeaveOn(leaves: LeaveRow[] | null | undefined, isoDate: string): boolean {
  return (leaves || []).some((l) => {
    const status = String(l.status ?? 'approved').toLowerCase();
    if (status !== 'approved') return false;
    const start = String(l.start_date || '').slice(0, 10);
    const end = String(l.end_date || l.start_date || '').slice(0, 10);
    return !!start && start <= isoDate && end >= isoDate;
  });
}

export const EMPLOYEE_STATUS_META: Record<
  EmployeeStatus,
  { label: string; emoji: string; badge: string; dot: string }
> = {
  active: {
    label: 'Active',
    emoji: '🟢',
    badge: 'bg-success-soft text-success-strong border-transparent',
    dot: 'bg-success',
  },
  on_leave: {
    label: 'On Leave',
    emoji: '🟡',
    badge: 'bg-warning-soft text-warning-strong border-transparent',
    dot: 'bg-warning',
  },
  inactive: {
    label: 'Inactive',
    emoji: '🔴',
    badge: 'bg-danger-soft text-danger-strong border-transparent',
    dot: 'bg-danger',
  },
};
